<template>
  <div class="page">
    <div class="hint text-caption">
      <v-icon small>mdi-information-outline</v-icon>
      Search and manage applicant profile, DEB and ABC details
    </div>

    <v-form @submit.prevent="searchStudent">
      <div class="search-row">
        <v-text-field
          v-model="application_id"
          placeholder="Application ID"
          outlined
          dense
          clearable
          hide-details
          style="max-width: 260px"
        />
        <v-btn
          type="submit"
          :loading="loading"
          :disabled="!application_id"
          outlined
          color="primary"
          class="text-none"
        >
          <v-icon left small>mdi-magnify</v-icon>
          Search
        </v-btn>
      </div>
    </v-form>

    <template v-if="studentData">
      <v-card outlined rounded="lg">
        <div class="card-header">
          <div class="icon-wrap">
            <v-icon color="#185FA5">mdi-account-outline</v-icon>
          </div>
          <div>
            <div class="card-title">Applicant information</div>
            <div class="card-subtitle">Update applicant profile details</div>
          </div>
        </div>

        <div class="card-body">
          <div class="field-grid">
            <div class="field-group">
              <span class="field-label">Application ID</span>
              <v-text-field
                :value="studentData.application_id"
                outlined
                dense
                readonly
                hide-details
                class="readonly-field"
              />
            </div>
            <div class="field-group">
              <span class="field-label">Applicant name</span>
              <v-text-field
                v-model="formData.name"
                outlined
                readonly
                dense
                hide-details
                class="readonly-field"
              />
            </div>
            <div class="field-group">
              <span class="field-label">Email address</span>
              <v-text-field
                v-model="formData.email"
                outlined
                dense
                hide-details
                :class="{
                  'changed-field': originalData.email !== formData.email,
                }"
              />
              <div
                v-if="originalData.email !== formData.email"
                class="change-hint"
              >
                <span class="old">{{ originalData.email }}</span>
                <v-icon x-small class="mx-1">mdi-arrow-right</v-icon>
                <span class="new">{{ formData.email }}</span>
              </div>
            </div>
            <div class="field-group">
              <span class="field-label">Mobile number</span>
              <v-text-field
                v-model="formData.mobile_no"
                outlined
                dense
                hide-details
                maxlength="10"
                :class="{
                  'changed-field':
                    originalData.mobile_no !== formData.mobile_no,
                }"
              />
              <div
                v-if="originalData.mobile_no !== formData.mobile_no"
                class="change-hint"
              >
                <span class="old">{{ originalData.mobile_no }}</span>
                <v-icon x-small class="mx-1">mdi-arrow-right</v-icon>
                <span class="new">{{ formData.mobile_no }}</span>
              </div>
            </div>
          </div>
        </div>
      </v-card>

      <v-card outlined rounded="lg">
        <div class="card-header">
          <div class="icon-wrap">
            <v-icon color="#534AB7">mdi-shield-account-outline</v-icon>
          </div>
          <div>
            <div class="card-title">DEB &amp; ABC details</div>
            <div class="card-subtitle">
              Update identity verification details
            </div>
          </div>
        </div>
        <div class="card-body">
          <div class="field-grid two-col">
            <div class="field-group">
              <span class="field-label">DEB ID</span>
              <v-text-field
                v-model="formData.deb_id"
                outlined
                dense
                maxlength="12"
                hide-details="auto"
                :readonly="studentData.deb_validated == true"
                :error-messages="debErrors"
                :class="{
                  'changed-field': originalData.deb_id !== formData.deb_id,
                  'readonly-field': studentData.deb_validated,
                }"
                @input="validateDebId"
              />
              <div v-if="formData.deb_id" class="badge-active">
                <v-icon x-small color="#3B6D11"
                  >mdi-check-circle-outline</v-icon
                >
                Active
              </div>
              <div
                v-if="
                  originalData.deb_id && originalData.deb_id !== formData.deb_id
                "
                class="change-hint"
              >
                <span class="old">{{ originalData.deb_id }}</span>
                <v-icon x-small class="mx-1">mdi-arrow-right</v-icon>
                <span class="new">{{ formData.deb_id }}</span>
              </div>
            </div>

            <div class="field-group">
              <span class="field-label">ABC ID</span>
              <v-text-field
                v-model="formData.abc_id"
                outlined
                dense
                maxlength="12"
                :readonly="studentData.deb_validated"
                hide-details="auto"
                :error-messages="abcErrors"
                :class="{
                  'changed-field': originalData.abc_id !== formData.abc_id,
                  'readonly-field': studentData.deb_validated,
                }"
                @input="validateAbcId"
              />
              <div v-if="formData.abc_id" class="badge-active">
                <v-icon x-small color="#3B6D11"
                  >mdi-check-circle-outline</v-icon
                >
                Active
              </div>
              <div
                v-if="
                  originalData.abc_id && originalData.abc_id !== formData.abc_id
                "
                class="change-hint"
              >
                <span class="old">{{ originalData.abc_id }}</span>
                <v-icon x-small class="mx-1">mdi-arrow-right</v-icon>
                <span class="new">{{ formData.abc_id }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="card-footer">
          <v-btn
            v-if="isFormDirty"
            text
            class="text-none mr-2"
            @click="resetForm"
          >
            Cancel
          </v-btn>
          <v-btn
            :loading="updateLoading"
            :disabled="!isFormValid || !isFormDirty"
            class="primary white--text"
            @click="openConfirmDialog"
          >
            Save changes
          </v-btn>
        </div>
      </v-card>
    </template>

    <v-dialog v-model="confirmDialog" max-width="480" persistent>
      <v-card rounded="lg" class="confirm-card">
        <div class="confirm-header">
          <span class="confirm-title">Confirm update</span>
          <v-btn icon small @click="confirmDialog = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="confirm-body">
          <p class="confirm-desc">
            Please verify the updated details before saving.
          </p>

          <v-simple-table dense class="diff-table">
            <tbody>
              <tr>
                <td class="row-label">Name</td>
                <td>{{ formData.name }}</td>
              </tr>
              <tr>
                <td class="row-label">Email</td>
                <td>
                  <template v-if="originalData.email !== formData.email">
                    <span class="old-val">{{ originalData.email || "-" }}</span>
                    <v-icon x-small class="mx-1">mdi-arrow-right</v-icon>
                    <span class="new-val">{{ formData.email }}</span>
                  </template>
                  <template v-else>{{ formData.email || "-" }}</template>
                </td>
              </tr>
              <tr>
                <td class="row-label">Mobile</td>
                <td>
                  <template
                    v-if="originalData.mobile_no !== formData.mobile_no"
                  >
                    <span class="old-val">{{
                      originalData.mobile_no || "-"
                    }}</span>
                    <v-icon x-small class="mx-1">mdi-arrow-right</v-icon>
                    <span class="new-val">{{ formData.mobile_no }}</span>
                  </template>
                  <template v-else>{{ formData.mobile_no || "-" }}</template>
                </td>
              </tr>
              <tr>
                <td class="row-label">DEB ID</td>
                <td>
                  <template v-if="originalData.deb_id !== formData.deb_id">
                    <span class="old-val">{{
                      originalData.deb_id || "-"
                    }}</span>
                    <v-icon x-small class="mx-1">mdi-arrow-right</v-icon>
                    <span class="new-val">{{ formData.deb_id }}</span>
                  </template>
                  <template v-else>{{ formData.deb_id || "-" }}</template>
                </td>
              </tr>
              <tr>
                <td class="row-label">ABC ID</td>
                <td>
                  <template v-if="originalData.abc_id !== formData.abc_id">
                    <span class="old-val">{{
                      originalData.abc_id || "-"
                    }}</span>
                    <v-icon x-small class="mx-1">mdi-arrow-right</v-icon>
                    <span class="new-val">{{ formData.abc_id }}</span>
                  </template>
                  <template v-else>{{ formData.abc_id || "-" }}</template>
                </td>
              </tr>
            </tbody>
          </v-simple-table>

          <div class="warn-box">
            <v-icon small color="#854F0B" class="mr-2" style="flex-shrink: 0">
              mdi-alert-outline
            </v-icon>
            This action will update applicant information permanently and cannot
            be undone.
          </div>
        </div>

        <div class="confirm-footer">
          <v-btn text class="text-none mr-2" @click="confirmDialog = false"
            >Cancel</v-btn
          >
          <v-btn
            :loading="updateLoading"
            class="primary white--text"
            @click="updateDebAbc"
          >
            Confirm update
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { rules } from "@/helpers/rules";

export default {
  name: "DebUpdate",
  data() {
    return {
      rules,
      application_id: "",
      loading: false,
      searched: false,
      updateLoading: false,
      confirmDialog: false,
      studentData: null,
      formData: { name: "", email: "", mobile_no: "", deb_id: "", abc_id: "" },
      originalData: {
        name: "",
        email: "",
        mobile_no: "",
        deb_id: "",
        abc_id: "",
      },
      debErrors: [],
      abcErrors: [],
    };
  },

  computed: {
    isFormDirty() {
      return (
        JSON.stringify(this.formData) !== JSON.stringify(this.originalData)
      );
    },
    isFormValid() {
      return (
        this.formData.name &&
        this.formData.email &&
        this.formData.mobile_no &&
        this.formData.deb_id &&
        this.formData.abc_id &&
        this.debErrors.length === 0 &&
        this.abcErrors.length === 0
      );
    },
  },

  methods: {
    async searchStudent() {
      if (!this.application_id) return;
      this.loading = true;
      this.searched = true;
      this.studentData = null;
      try {
        const res = await this.$axios.get(
          `/get_applicant_detail_by_application_id?application_id=${this.application_id}`,
        );
        if (res.data?.data) {
          this.studentData = res.data.data;
          this.formData = {
            name: this.studentData.name || "",
            email: this.studentData.email || "",
            mobile_no: this.studentData.mobile_no || "",
            deb_id: this.studentData.deb_id || "",
            abc_id: this.studentData.abc_id || "",
          };
          this.originalData = { ...this.formData };
        }
      } catch {
        this.$store.commit("showSnackbar", {
          message: "Failed to fetch applicant details",
          color: "error",
        });
      } finally {
        this.loading = false;
      }
    },

    validateDebId() {
      this.debErrors = [];
      if (!this.formData.deb_id) {
        this.debErrors.push("DEB ID is required");
      } else if (!/^[A-Z0-9]{11,12}$/.test(this.formData.deb_id)) {
        this.debErrors.push("Enter a valid DEB ID");
      }
    },

    validateAbcId() {
      this.formData.abc_id = this.formData.abc_id.toUpperCase();
      this.abcErrors = [];
      if (!this.formData.abc_id) {
        this.abcErrors.push("ABC ID is required");
      } else if (!/^[A-Z0-9]{11,12}$/.test(this.formData.abc_id)) {
        this.abcErrors.push("Enter a valid ABC ID");
      }
    },

    openConfirmDialog() {
      this.confirmDialog = true;
    },

    async updateDebAbc() {
      this.updateLoading = true;
      try {
        const formData = new FormData();
        formData.append("attrs", JSON.stringify(this.formData));
        const res = await this.$axios.put(
          `/applicant_details/${this.studentData.id}`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        if (res.data?.data) {
          this.originalData = { ...this.formData };
          this.studentData = { ...this.studentData, ...this.formData };
          this.confirmDialog = false;
          this.$store.commit("showSnackbar", {
            message: "Applicant details updated successfully",
            color: "success",
          });
        }
      } catch {
        this.$store.commit("showSnackbar", {
          message: "Failed to update applicant details",
          color: "error",
        });
      } finally {
        this.updateLoading = false;
      }
    },

    resetForm() {
      this.formData = { ...this.originalData };
      this.$store.commit("showSnackbar", {
        message: "Changes discarded",
        color: "info",
      });
      this.application_id = "";
      this.studentData = null;
      this.searchStudent();
    },
  },
};
</script>

<style scoped>
.page {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.hint {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #757575;
}
.search-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.card-header {
  padding: 14px 18px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  gap: 12px;
}
.icon-wrap {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.icon-wrap.blue {
  background: #e6f1fb;
}
.icon-wrap.purple {
  background: #eeedfe;
}

.card-title {
  font-size: 14px;
  font-weight: 600;
  color: #212121;
}
.card-subtitle {
  font-size: 12px;
  color: #757575;
}

.card-body {
  padding: 16px 18px;
}
.card-footer {
  padding: 12px 18px;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;
  display: flex;
  justify-content: flex-end;
}

.field-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
}
.field-grid.two-col {
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.field-label {
  font-size: 11px;
  color: #9e9e9e;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.change-hint {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  margin-top: 2px;
}
.change-hint .old {
  color: #9e9e9e;
  text-decoration: line-through;
}
.change-hint .new {
  color: #1565c0;
  font-weight: 600;
}

.badge-active {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  background: #eaf3de;
  color: #3b6d11;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  width: fit-content;
  margin-top: 4px;
}

.readonly-field ::v-deep input {
  color: #9e9e9e !important;
}
.changed-field ::v-deep fieldset {
  border-color: #1976d2 !important;
}

/* Confirm Dialog */
.confirm-card {
  overflow: hidden;
}
.confirm-header {
  padding: 14px 16px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.confirm-title {
  font-size: 14px;
  font-weight: 600;
  color: #212121;
}
.confirm-body {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.confirm-desc {
  font-size: 13px;
  color: #757575;
}
.confirm-footer {
  padding: 12px 16px;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;
  display: flex;
  justify-content: flex-end;
}

.diff-table {
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #f0f0f0;
}
.row-label {
  font-weight: 600 !important;
  font-size: 13px;
  color: #616161 !important;
  width: 100px;
}
.old-val {
  color: #9e9e9e;
  text-decoration: line-through;
  font-size: 13px;
}
.new-val {
  color: #1565c0;
  font-weight: 600;
  font-size: 13px;
}

.warn-box {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: #faeeda;
  border: 1px solid #ef9f27;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 12px;
  color: #633806;
}

@media print {
  .page {
    display: none;
  }
}
@media (max-width: 600px) {
  .page {
    padding: 12px;
  }
}
</style>
